import type { Workbook, Worksheet } from 'exceljs'
import { getDisplayPlatformName } from '../types/donation'
import { GiftAidDonor, GiftAidDonorTransaction } from './gift-aid-api'

// A date range for a Gift Aid claim. Dates are YYYY-MM-DD and inclusive.
export interface ClaimPeriod {
  id: string
  label: string
  from: string
  to: string
}

// A donor narrowed to the donations in a claim period
export interface PeriodDonor {
  donor: GiftAidDonor
  transactions: GiftAidDonorTransaction[]
  total: number // pence
  coveredTotal: number // pence, donations Gift Aid can be claimed on
  firstCoveredDate?: string // YYYY-MM-DD
  lastCoveredDate?: string // YYYY-MM-DD
}

export type SheetId = 'hmrc' | 'totals' | 'donations'

const toIsoDate = (d: Date): string => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// The donors API returns donations from the last 4 years
export const lookbackStart = (today: Date = new Date()): string => {
  const d = new Date(today)
  d.setFullYear(d.getFullYear() - 4)
  return toIsoDate(d)
}

// UK tax years run 6 April to 5 April
const taxYearStarting = (startYear: number): ClaimPeriod => ({
  id: `ty-${startYear}`,
  label: `Tax year ${startYear}–${String(startYear + 1).slice(-2)}`,
  from: `${startYear}-04-06`,
  to: `${startYear + 1}-04-05`,
})

// Period options: the whole 4-year window, then each tax year that fits inside it, newest first
export const claimPeriodOptions = (today: Date = new Date()): ClaimPeriod[] => {
  const todayIso = toIsoDate(today)
  const earliest = lookbackStart(today)
  const currentStart = todayIso >= `${today.getFullYear()}-04-06` ? today.getFullYear() : today.getFullYear() - 1

  const options: ClaimPeriod[] = [{ id: 'all', label: 'Last 4 years', from: earliest, to: todayIso }]
  for (let year = currentStart; year > currentStart - 4; year--) {
    const period = taxYearStarting(year)
    if (period.from < earliest) break
    if (year === currentStart) {
      options.push({ ...period, label: `${period.label} (so far)`, to: todayIso })
    } else {
      options.push(period)
    }
  }
  return options
}

export const customPeriod = (from: string, to: string): ClaimPeriod => ({
  id: 'custom',
  label: `${formatShortDate(from)} – ${formatShortDate(to)}`,
  from,
  to,
})

// Short name used in file names, e.g. "2025-26" or "2025-04-06_to_2025-09-30"
export const periodFileTag = (period: ClaimPeriod): string => {
  if (period.id.startsWith('ty-')) {
    const start = Number(period.id.slice(3))
    return `${start}-${String(start + 1).slice(-2)}`
  }
  if (period.id === 'all') return `Last-4-years_to_${period.to}`
  return `${period.from}_to_${period.to}`
}

export const donorsInPeriod = (donors: GiftAidDonor[], period: ClaimPeriod): PeriodDonor[] => {
  const result: PeriodDonor[] = []
  for (const donor of donors) {
    const transactions = donor.transactions.filter(t => t.date >= period.from && t.date <= period.to)
    if (transactions.length === 0) continue
    let total = 0
    let coveredTotal = 0
    let firstCoveredDate: string | undefined
    let lastCoveredDate: string | undefined
    for (const t of transactions) {
      total += t.amount
      if (t.giftAidCovered) {
        coveredTotal += t.amount
        if (!firstCoveredDate || t.date < firstCoveredDate) firstCoveredDate = t.date
        if (!lastCoveredDate || t.date > lastCoveredDate) lastCoveredDate = t.date
      }
    }
    result.push({ donor, transactions, total, coveredTotal, firstCoveredDate, lastCoveredDate })
  }
  return result
}

// Donors whose period donations can go on an HMRC claim
export const claimableDonors = (periodDonors: PeriodDonor[]): PeriodDonor[] =>
  periodDonors.filter(p => p.donor.consented && p.coveredTotal > 0)

// HMRC schedule field checks

const HMRC_MAX_TITLE = 4
const HMRC_MAX_NAME = 35
const HMRC_MAX_HOUSE = 40

export interface HouseField {
  value: string
  problem?: string
  issue?: HmrcIssueCode
}

// HMRC wants the house name or number only, not the full address
export const houseNameOrNumber = (addressLine1: string): HouseField => {
  const line = addressLine1.trim()
  if (!line) return { value: '', problem: 'No address', issue: 'no_address' }

  const number = line.match(/^(\d+[A-Za-z]?(?:\s*[-/]\s*\d+[A-Za-z]?)?)\b/)
  if (number) return { value: number[1].replace(/\s+/g, '') }

  // No leading number: probably a house name, or a flat
  const firstPart = line.split(',')[0].trim()
  if (firstPart.length > HMRC_MAX_HOUSE) {
    return { value: firstPart.slice(0, HMRC_MAX_HOUSE), problem: `Longer than ${HMRC_MAX_HOUSE} characters, check it`, issue: 'house_too_long' }
  }
  return { value: firstPart, problem: 'No house number found, check this is the house name', issue: 'house_unreadable' }
}

const fieldProblem = (value: string, max: number, label: string, required: boolean): string | undefined => {
  if (required && !value.trim()) return `${label} is missing`
  if (value.length > max) return `${label} is longer than ${max} characters`
  return undefined
}

// The kinds of problem the HMRC claim sheet highlights, summarised in the Export dialog
export type HmrcIssueCode =
  | 'first_name_space'
  | 'house_unreadable'
  | 'no_address'
  | 'house_too_long'
  | 'no_postcode'
  | 'missing_first_name'
  | 'missing_last_name'
  | 'name_too_long'
  | 'title_too_long'

// How each problem reads in the Export dialog, in the order to list ties
export const HMRC_ISSUES: { code: HmrcIssueCode; what: (n: number) => string; why: string }[] = [
  { code: 'first_name_space', what: n => `${n} ${n === 1 ? 'has' : 'have'} a space in their first name`, why: 'HMRC wants one first name or an initial. Move the extra words into the last name.' },
  { code: 'house_unreadable', what: n => `${n} ${n === 1 ? 'has' : 'have'} no house number we can read`, why: 'HMRC wants the house number or name. Check this is the house name.' },
  { code: 'no_address', what: n => `${n} ${n === 1 ? 'has' : 'have'} no address`, why: 'HMRC needs the house number or name.' },
  { code: 'house_too_long', what: n => `${n} ${n === 1 ? 'has a house name' : 'have house names'} longer than ${HMRC_MAX_HOUSE} characters`, why: "That's HMRC's limit for the house name or number." },
  { code: 'no_postcode', what: n => `${n} ${n === 1 ? 'has' : 'have'} no postcode`, why: 'HMRC needs a postcode for a UK address.' },
  { code: 'missing_first_name', what: n => `${n} ${n === 1 ? 'has' : 'have'} no first name`, why: 'HMRC needs a first name or initial.' },
  { code: 'missing_last_name', what: n => `${n} ${n === 1 ? 'has' : 'have'} no last name`, why: 'HMRC needs a last name.' },
  { code: 'name_too_long', what: n => `${n} ${n === 1 ? 'has a first or last name' : 'have first or last names'} longer than ${HMRC_MAX_NAME} characters`, why: "That's HMRC's limit for names." },
  { code: 'title_too_long', what: n => `${n} ${n === 1 ? 'has a title' : 'have titles'} longer than ${HMRC_MAX_TITLE} characters`, why: "That's HMRC's limit for titles (Mr, Mrs, Miss, Dr)." },
]

export interface HmrcRow {
  title: string
  firstName: string
  lastName: string
  house: string
  postcode: string
  date: string // DD/MM/YY, the donor's latest covered donation
  earliestDate: string // YYYY-MM-DD, the donor's earliest covered donation (for Box 1)
  amount: number // pounds
  // Note for each highlighted cell, by column
  problems: Partial<Record<'title' | 'firstName' | 'lastName' | 'house' | 'postcode', string>>
  // The kinds of problem on this row, for the Export dialog's summary
  issues: HmrcIssueCode[]
}

// DD/MM/YY, as HMRC's Gift Aid schedule expects
const hmrcDate = (iso: string): string => {
  const [y, m, d] = iso.split('-')
  return `${d}/${m}/${y.slice(-2)}`
}

export const buildHmrcRows = (periodDonors: PeriodDonor[]): HmrcRow[] =>
  claimableDonors(periodDonors)
    .slice()
    .sort((a, b) => `${a.donor.lastName} ${a.donor.firstName}`.toLowerCase().localeCompare(`${b.donor.lastName} ${b.donor.firstName}`.toLowerCase()))
    .map(({ donor, coveredTotal, firstCoveredDate, lastCoveredDate }) => {
      const house = houseNameOrNumber(donor.addressLine1)
      // HMRC: no spaces at the start or end, and a space instead of a hyphen in surnames
      const title = donor.title.trim()
      const firstName = donor.firstName.trim()
      const lastName = donor.lastName.replace(/-/g, ' ').replace(/\s+/g, ' ').trim()
      const problems: HmrcRow['problems'] = {
        title: fieldProblem(title, HMRC_MAX_TITLE, 'Title', false),
        firstName:
          fieldProblem(firstName, HMRC_MAX_NAME, 'First name', true) ||
          (/\s/.test(firstName) ? 'HMRC wants one first name with no spaces, or an initial' : undefined),
        lastName: fieldProblem(lastName, HMRC_MAX_NAME, 'Last name', true),
        house: house.problem,
        postcode: donor.postcode.trim() ? undefined : 'Postcode is missing',
      }
      const issues = new Set<HmrcIssueCode>()
      if (title.length > HMRC_MAX_TITLE) issues.add('title_too_long')
      if (!firstName) issues.add('missing_first_name')
      else if (/\s/.test(firstName)) issues.add('first_name_space')
      if (!lastName) issues.add('missing_last_name')
      if (firstName.length > HMRC_MAX_NAME || lastName.length > HMRC_MAX_NAME) issues.add('name_too_long')
      if (house.issue) issues.add(house.issue)
      if (!donor.postcode.trim()) issues.add('no_postcode')
      return {
        title,
        firstName,
        lastName,
        house: house.value,
        postcode: donor.postcode.trim().toUpperCase(),
        date: lastCoveredDate ? hmrcDate(lastCoveredDate) : '',
        earliestDate: firstCoveredDate || '',
        amount: coveredTotal / 100,
        problems,
        issues: HMRC_ISSUES.map(i => i.code).filter(code => issues.has(code)),
      }
    })

export const countHmrcProblems = (rows: HmrcRow[]): number =>
  rows.filter(r => r.issues.length > 0).length

export interface HmrcIssueSummary {
  code: HmrcIssueCode
  what: string
  why: string
  donors: string[] // "Dawud Bin Awais", or "Yusuf Patel: Al Noor House" for house problems
}

// Problems grouped by kind, most donors first, for the Export dialog. A donor with two
// problems appears under both.
export const summarizeHmrcIssues = (rows: HmrcRow[]): HmrcIssueSummary[] => {
  const byCode = new Map<HmrcIssueCode, string[]>()
  for (const r of rows) {
    const name = [r.firstName, r.lastName].filter(Boolean).join(' ') || 'No name'
    for (const code of r.issues) {
      const label = code === 'house_unreadable' && r.house ? `${name}: ${r.house}` : name
      byCode.set(code, [...(byCode.get(code) || []), label])
    }
  }
  return HMRC_ISSUES
    .filter(i => byCode.has(i.code))
    .map(i => {
      const donors = byCode.get(i.code) || []
      return { code: i.code, what: i.what(donors.length), why: i.why, donors }
    })
    .sort((a, b) => b.donors.length - a.donors.length)
}

// Formatting

const formatShortDate = (iso: string): string =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

const ukDate = (iso: string): string => {
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

const giftAidOn = (pence: number): number => Math.round(pence * 0.25) / 100

// Downloads

export const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const csvCell = (value: string | number): string => {
  const s = String(value)
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

// HMRC's schedule takes up to 1,000 lines per claim
export const HMRC_MAX_ROWS = 1000

// Columns match the donations schedule table in HMRC's Gift Aid schedule template
// (Gift-Aid-Schedule-Excel.ods). `field` links a column to its problem note.
const HMRC_SHEET_COLUMNS: {
  header: string
  width: number
  field?: keyof HmrcRow['problems']
  value: (r: HmrcRow, index: number) => string | number
}[] = [
  { header: 'Item', width: 6, value: (_r, i) => i + 1 },
  { header: 'Title', width: 8, field: 'title', value: r => r.title },
  { header: 'First name', width: 18, field: 'firstName', value: r => r.firstName },
  { header: 'Last name', width: 20, field: 'lastName', value: r => r.lastName },
  { header: 'House name or number', width: 22, field: 'house', value: r => r.house },
  { header: 'Postcode', width: 11, field: 'postcode', value: r => r.postcode },
  { header: 'Aggregated donations', width: 20, value: () => '' },
  { header: 'Sponsored event', width: 15, value: () => '' },
  { header: 'Donation date', width: 13, value: r => r.date },
  { header: 'Amount', width: 11, value: r => r.amount },
]

export const HMRC_COLUMNS = HMRC_SHEET_COLUMNS.map(c => c.header)

// Box 1 on HMRC's schedule: the earliest donation date in the claim
export const earliestDonationDate = (rows: HmrcRow[]): string => {
  const dates = rows.map(r => r.earliestDate).filter(Boolean).sort()
  return dates.length ? hmrcDate(dates[0]) : ''
}

export const buildHmrcCsv = (rows: HmrcRow[]): Blob => {
  const lines = [HMRC_COLUMNS.map(csvCell).join(',')]
  rows.forEach((r, i) => {
    lines.push(HMRC_SHEET_COLUMNS.map(c => {
      const v = c.value(r, i)
      return csvCell(typeof v === 'number' && c.header === 'Amount' ? v.toFixed(2) : v)
    }).join(','))
  })
  // BOM so Excel reads names with accents correctly
  return new Blob(['﻿' + lines.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8' })
}

// Workbook

const HEADER_FILL = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFF1F5F4' } }
const PROBLEM_FILL = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFFEF3C7' } }
const CONSENTED_FILL = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFF0FDFA' } }
const NOT_CONSENTED_FILL = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFFFFBEB' } }
const POUNDS = '£#,##0.00'

const styleHeader = (sheet: Worksheet) => {
  const header = sheet.getRow(1)
  header.font = { bold: true }
  header.eachCell(cell => {
    cell.fill = HEADER_FILL
  })
  sheet.views = [{ state: 'frozen', ySplit: 1 }]
}

const addHmrcSheet = (wb: Workbook, rows: HmrcRow[]) => {
  const sheet = wb.addWorksheet('HMRC claim')
  sheet.columns = HMRC_SHEET_COLUMNS.map(c => ({
    header: c.header,
    width: c.width,
    ...(c.header === 'Amount' ? { style: { numFmt: '0.00' } } : {}),
  }))
  styleHeader(sheet)

  rows.forEach((r, index) => {
    const row = sheet.addRow(HMRC_SHEET_COLUMNS.map(c => c.value(r, index)))
    HMRC_SHEET_COLUMNS.forEach((c, i) => {
      const problem = c.field && r.problems[c.field]
      if (!problem) return
      const cell = row.getCell(i + 1)
      cell.fill = PROBLEM_FILL
      cell.note = problem
    })
  })

  // Totals and Box 1 sit below a blank row, apart from the donation rows
  const total = rows.reduce((sum, r) => sum + r.amount, 0)
  const lastColumn = HMRC_SHEET_COLUMNS.length
  const labelColumn = 4
  sheet.addRow([])
  const summary: [string, string | number][] = [
    ['Total donations', total],
    ['Gift Aid claimable (25%)', Math.round(total * 25) / 100],
    ['Box 1: earliest donation date in the period of claim', earliestDonationDate(rows)],
  ]
  for (const [label, value] of summary) {
    const row = sheet.addRow([])
    row.getCell(labelColumn).value = label
    row.getCell(lastColumn).value = value
    row.font = { bold: true }
  }
}

const addTotalsSheet = (wb: Workbook, periodDonors: PeriodDonor[]) => {
  const sheet = wb.addWorksheet('Donor totals')
  sheet.columns = [
    { header: 'Consent', width: 10 },
    { header: 'Title', width: 7 },
    { header: 'First name', width: 16 },
    { header: 'Last name', width: 16 },
    { header: 'Email', width: 26 },
    { header: 'Address line 1', width: 26 },
    { header: 'Address line 2', width: 18 },
    { header: 'City', width: 14 },
    { header: 'Postcode', width: 11 },
    { header: 'Donations', width: 10 },
    { header: 'Total', width: 12, style: { numFmt: POUNDS } },
    { header: 'Gift Aid covered', width: 15, style: { numFmt: POUNDS } },
    { header: 'Gift Aid (25%)', width: 13, style: { numFmt: POUNDS } },
    { header: 'Form last generated', width: 18 },
  ]
  styleHeader(sheet)

  const groups = [
    { consented: true, label: 'Consented', fill: CONSENTED_FILL },
    { consented: false, label: 'Not consented', fill: NOT_CONSENTED_FILL },
  ]
  for (const group of groups) {
    const members = periodDonors
      .filter(p => p.donor.consented === group.consented)
      .sort((a, b) => b.total - a.total)
    const total = members.reduce((sum, p) => sum + p.total, 0)
    const covered = members.reduce((sum, p) => sum + p.coveredTotal, 0)

    const heading = group.consented
      ? `${group.label}: ${members.length} donors · £${(total / 100).toFixed(2)} donated · £${giftAidOn(covered).toFixed(2)} Gift Aid`
      : `${group.label}: ${members.length} donors · £${(total / 100).toFixed(2)} donated · £${giftAidOn(total).toFixed(2)} possible if they consent`
    const headingRow = sheet.addRow([heading])
    headingRow.font = { bold: true }
    sheet.mergeCells(headingRow.number, 1, headingRow.number, 14)
    headingRow.getCell(1).fill = group.fill

    for (const p of members) {
      const d = p.donor
      sheet.addRow([
        d.consented ? 'Yes' : 'No',
        d.title,
        d.firstName,
        d.lastName,
        d.email,
        d.addressLine1,
        d.addressLine2,
        d.city,
        d.postcode,
        p.transactions.length,
        p.total / 100,
        p.coveredTotal / 100,
        giftAidOn(p.coveredTotal),
        d.lastGeneratedAt ? ukDate(d.lastGeneratedAt) : 'Never',
      ])
    }
    sheet.addRow([])
  }
}

const addDonationsSheet = (wb: Workbook, periodDonors: PeriodDonor[]) => {
  const sheet = wb.addWorksheet('All donations')
  sheet.columns = [
    { header: 'Date', width: 12 },
    { header: 'Reference', width: 16 },
    { header: 'Title', width: 7 },
    { header: 'First name', width: 16 },
    { header: 'Last name', width: 16 },
    { header: 'Postcode', width: 11 },
    { header: 'Donor consent', width: 14 },
    { header: 'Gift Aid ticked (app)', width: 19 },
    { header: 'Gift Aid covered', width: 16 },
    { header: 'Donation type', width: 22 },
    { header: 'Platform', width: 20 },
    { header: 'Amount', width: 11, style: { numFmt: POUNDS } },
    { header: 'Declaration ID', width: 38 },
  ]
  styleHeader(sheet)

  const rows = periodDonors.flatMap(p => p.transactions.map(t => ({ donor: p.donor, t })))
  rows.sort((a, b) => (a.t.date < b.t.date ? 1 : a.t.date > b.t.date ? -1 : 0))
  for (const { donor, t } of rows) {
    sheet.addRow([
      ukDate(t.date),
      t.reference || '',
      donor.title,
      donor.firstName,
      donor.lastName,
      donor.postcode,
      donor.consented ? 'Yes' : 'No',
      t.giftAid ? 'Yes' : 'No',
      t.giftAidCovered ? 'Yes' : 'No',
      t.donationType,
      getDisplayPlatformName(t.platform, t.device),
      t.amount / 100,
      donor.lastDeclarationId || '',
    ])
  }
  sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: 13 } }
}

export const buildWorkbook = async (periodDonors: PeriodDonor[], sheets: SheetId[]): Promise<Blob> => {
  // Loaded on demand so the spreadsheet library isn't in the main bundle
  const mod: any = await import('exceljs')
  const ExcelJS = mod.default ?? mod
  const wb: Workbook = new ExcelJS.Workbook()
  wb.creator = 'Ross Street Masjid Admin'
  wb.created = new Date()

  if (sheets.includes('hmrc')) addHmrcSheet(wb, buildHmrcRows(periodDonors))
  if (sheets.includes('totals')) addTotalsSheet(wb, periodDonors)
  if (sheets.includes('donations')) addDonationsSheet(wb, periodDonors)

  const buffer = await wb.xlsx.writeBuffer()
  return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
}
